import { Form } from "@circle-react-uikit/Form";
import { LinkInput } from "@circle-react-uikit/Form/LinkInput";
import { Checkbox } from "@circleco/compass/components/Checkbox";
import { Popover } from "@circleco/compass/components/Popover";
import { Typography } from "@circleco/compass/components/Typography";
import { useMemo } from "react";

import { t } from "@/i18n-js/instance";

import { buildSelectionAnchor } from "./buildSelectionAnchor";
import { LinkPopoverFooter } from "./LinkPopoverFooter";
import type { LinkPopoverProps } from "./types";
import { useLinkPopoverForm } from "./useLinkPopoverForm";

const FORM_ID = "add-tiptap-link-form";

export const AddTipTapLinkModalComponent = ({
  anchor,
  editor,
  isEmailEditor = false,
  open,
  onOpenChange,
}: LinkPopoverProps) => {
  const handleClose = (): void => onOpenChange(false);

  const {
    isEditingExistingLink,
    previousURL,
    shouldOpenInNewTab,
    setShouldOpenInNewTab,
    handleRemoveLink,
    handleSubmit,
  } = useLinkPopoverForm({
    editor,
    isEmailEditor,
    onClose: handleClose,
  });

  const resolvedAnchor = useMemo(
    () => (open ? (anchor ?? buildSelectionAnchor(editor)) : null),
    [open, editor, anchor],
  );

  const title = isEditingExistingLink
    ? t("tiptap.extensions.link.edit")
    : t("tiptap.extensions.link.add");

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      anchor={resolvedAnchor ?? undefined}
      side="bottom"
      align="start"
      sideOffset={8}
    >
      <div className="flex w-80 flex-col gap-3">
        <Typography variant="label-md" component="span">
          {title}
        </Typography>
        <Form
          id={FORM_ID}
          onSubmit={handleSubmit}
          defaultValues={{ url: previousURL }}
          autoComplete="off"
        >
          <Form.Item
            hideDescription
            hideBorder
            fullWidth
            name="url"
            label={t("tiptap.extensions.link.link")}
            placeholder={t("tiptap.extensions.link.placeholder")}
            className="!pt-0"
          >
            <LinkInput name="url" />
          </Form.Item>
        </Form>
        <Checkbox
          label={t("tiptap.extensions.link.open_in_new_tab")}
          checked={shouldOpenInNewTab}
          onCheckedChange={setShouldOpenInNewTab}
        />
        <LinkPopoverFooter
          formId={FORM_ID}
          isEditingExistingLink={isEditingExistingLink}
          onCancel={handleClose}
          onRemove={handleRemoveLink}
        />
      </div>
    </Popover>
  );
};
